import { Box, Typography } from "@mui/material";
import { usePopup } from "@nodeon/react-components";
import { saveAs } from "file-saver";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useFileApi, useProjectApi, WPRListDto } from "~/api/GeneratedApi";
import { useDeleteFile } from "~/components/file-explorer/useDeleteFile";
import GenericTable, {
  CustomColumn,
  CustomTableProps,
} from "~/components/GenericTable";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { useProjectDetails } from "../hooks/useProjectDetails";
import { WPRProvider } from "./WPRContextProvider";
import { WPRStepper } from "./WPRStepper";

export const WPRTab = () => {
  // const { id } = useProjectDetails();
  const [showStepper, setShowStepper] = React.useState(false);
  const { t } = useTranslation("file-explorer");
  const { getWPRList } = useProjectApi();
  const { id } = useProjectDetails();
  const { confirm } = usePopup();
  const { data, refetch } = useCustomQuery({ key: "wpr-list", id }, () =>
    getWPRList(id),
  );
  const fileApi = useFileApi();
  const formatSize = useCallback(
    (bytes: number, decimals: number): string => {
      if (bytes === 0) return `0 ${t("bytes")}`;

      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = [t("bytes"), t("kb"), t("mb"), t("gb"), t("tb")];

      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
    },
    [t],
  );
  const columns = React.useMemo((): CustomColumn<WPRListDto>[] => {
    return [
      {
        field: "fullName",
        title: t("profile:node-name"),
        width: "min-content",
        render: ({ latest, fullName }) => (
          <Box sx={{ display: "flex", gap: "10px" }}>
            <Typography>{fullName}</Typography>
            <Typography fontWeight="bold">
              {latest ? `(${t("general:latest")})` : null}
            </Typography>
          </Box>
        ),
      },
      {
        field: "bytes",
        title: t("file-explorer:table-file-size"),
        render: ({ bytes }) => <Typography>{formatSize(bytes, 2)}</Typography>,
      },
    ];
  }, [formatSize, t]);

  const { deleteFile } = useDeleteFile();

  const tableProps = React.useMemo(
    (): CustomTableProps<WPRListDto> => ({
      title: t("WPR:tableTitle"),
      options: {
        filtering: false,
        pageSize: 10,
        idSynonym: "id",
      },
      actions: [
        {
          icon: "download",
          tooltip: t("file-explorer:download"),
          onClick: (_e, rowData) => {
            if (Array.isArray(rowData)) return;
            const { id: fileId, fullName } = rowData;
            void fileApi.getFile(fileId).then((x) => {
              saveAs(x.data, fullName);
            });
          },
        },
        {
          icon: "delete",
          tooltip: t("general:delete"),
          onClick: (_e, d) => {
            if (Array.isArray(d)) return;
            confirm({
              title: t("general:are-you-sure"),
              description: t("general:delete-with-name", {
                name: d.fullName ?? "",
              }),
            })
              .then(() =>
                deleteFile(
                  {
                    fileId: d.id,
                  },
                  {
                    onSuccess: () => {
                      void refetch();
                    },
                  },
                ),
              )
              .catch(() => {});
          },
        },
        {
          icon: "add",
          tooltip: t("general:add"),
          onClick: () => setShowStepper(true),
          isFreeAction: true,
        },
      ],
    }),
    [confirm, deleteFile, fileApi, refetch, t],
  );

  if (showStepper)
    return (
      <WPRProvider>
        <WPRStepper setShowStepper={setShowStepper} />
      </WPRProvider>
    );

  return (
    <Box
      sx={{
        "& div": {
          boxShadow: "none",
        },
      }}
    >
      <GenericTable<WPRListDto>
        columns={columns}
        tableProps={tableProps}
        data={data ?? []}
      />
    </Box>
  );
};
